import React from 'react'
import PropTypes from 'prop-types'

import {createSelector} from 'reselect'
import {
  acttasksSelector,
  acttaskByCostSelector,
  hasCostDataSelector,
  mostExpensiveSelector,
  tasksWithCostSelector,
  collectionNameSelector
} from '../selectors/dataset'

import Enum from 'swift-enum'
import {sumBy} from 'lodash'

import { VictoryLegend, VictoryContainer, VictoryPie} from 'victory'

import Paper from 'material-ui/Paper'
import Typography from 'material-ui/Typography'
import { withStyles } from 'material-ui/styles'
import Grid from 'material-ui/Grid'
import ItemToolbar from './ItemToolbar'
import {chartColors, chartSizes, chartSelection as chartSelection_theme, framework as appTheme } from '../etc/theme'
import { blueGrey } from 'material-ui/colors'
import * as f from '../etc/formatters'
import TaskList from '../components/TaskList'
import {ClickableSlice} from '../etc/victory-utils'
import Divider from 'material-ui/Divider'

class Filter extends Enum {
  get title()
  {
    switch(this)
    {
      case Filter.fixed_cost: return 'Fixed Cost'
      case Filter.resource_cost: return 'Resource Cost'
      case Filter.none:
      default:
       return ''
    }
  }
}
Filter.case('fixed_cost', 'resource_cost')
Filter.case({none: ''})
Filter.freeze()

const defaultFilter = Filter.none
export {Filter, defaultFilter}


let totalFixedCostSelector = createSelector(
  acttasksSelector,
  acttasks => (sumBy(acttasks, acttask => acttask.fixed_cost))
)
let totalResourceCostSelector = createSelector(
  acttasksSelector,
  acttasks => (sumBy(acttasks, acttask => acttask.resource_cost))
)

// (dataset, current_filter) => [{}]
let costDataSelector = createSelector(
  totalFixedCostSelector,
  totalResourceCostSelector,
  (dataset, current_filter) => (current_filter),
  (fixed_cost, resource_cost, current_filter) => ([
    {x: 1, filter: Filter.fixed_cost, cost_type: 'Fixed Cost', y: fixed_cost, fill: chartColors.cost.fixed}, 
    {x: 2, filter: Filter.resource_cost, cost_type: 'Resource Cost', y: resource_cost, fill: chartColors.cost.resource}
  ].map((d) => ({
    ...d,
    stroke: chartSelection_theme.strokeColor ,      
    strokeWidth: (d.y != 0 && d.filter == current_filter) ? chartSelection_theme.strokeWidth : 0
  })))
)

// (dataset) => [{}]
let legendDataSelector = createSelector(
  totalFixedCostSelector,
  totalResourceCostSelector,
  (fixed_cost, resource_cost) => ([
    {name: `Fixed Cost: ${f.currency(fixed_cost)}` },
    {name: `Resource Cost: ${f.currency(resource_cost)}`},
    {name: `Total Cost: ${f.currency(fixed_cost + resource_cost)}`},
  ])
)

export let taglineSelector = createSelector(
  totalFixedCostSelector,
  totalResourceCostSelector,
  collectionNameSelector,
  (fixed_cost, resource_cost, collection_name) => (`${collection_name} Cost: ${f.currency(fixed_cost+resource_cost)}`)
)


let PieGraph =  ({dataset, radius, currentFilter, setCurrentFilter, ...rest}) => {
  let size = radius * 2
  return (
    <VictoryContainer width={size} height={size} responsive={false} {...rest}>
      <VictoryPie  width={size} height={size}
        padding={0}
        data={costDataSelector(dataset, currentFilter)}
        labels={() => null}
        standalone={false}
        dataComponent={<ClickableSlice 
          onClick={(datum) => {if (setCurrentFilter) setCurrentFilter(datum.filter)}}
        />}
      />
    </VictoryContainer>
  )
}

PieGraph.propTypes = {
  dataset: PropTypes.object.isRequired,
  radius: PropTypes.number.isRequired,
  currentFilter: PropTypes.instanceOf(Filter),
  setCurrentFilter: PropTypes.func,
}


// TODO Filter out task with zeros when chart is clicked 
// (dataset, current_filter) => [acttask]
export let filteredTasksSelector = createSelector(
  tasksWithCostSelector,
  acttaskByCostSelector, 
  (dataset, current_filter) => (current_filter),
  (with_cost, by_cost, current_filter) => {
    switch(current_filter) {
      case Filter.fixed_cost: return by_cost.fixed_cost
      case Filter.resource_cost: return by_cost.resource_cost
      case Filter.none:
      default:
       return with_cost
    }
  }
)

export let fields = [
  {
    id: 'wbs',
    label: 'Wbs',
    style: {
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis',
      width: '10%'
    }
  },
  {
    id: 'name',
    label: 'Name',
    style: {
      width: '400px',
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis'
    }
  },
  {
    id: 'resource_cost',
    label: 'Resource Cost',
    style: {
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis',
      width: '30%'      
    },
    hidden: {xsDown: true}
    
  },
  {
    id: 'fixed_cost',
    label: 'Fixed Cost',
    style: {
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis',
      width: '30%'      
    },
    hidden: {xsDown: true}
  },
  {
    id:  'total_cost',
    laberl: 'Total Cost',
    style: {
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis',
      width: '30%'      
    },
    hidden: {xsDown: true}

  }
]

export let LegendChart = ({dataset, ...rest}) => (
  <VictoryContainer height={140} responsive={false}>
  <VictoryLegend 
    colorScale={[chartColors.cost.fixed, chartColors.cost.resource, blueGrey[50]]}
    data={legendDataSelector(dataset)}
    standalone={false}
    {...rest}
  />
  </VictoryContainer>
)

export let MainChart = ({dataset, currentFilter, onSetFilter, radius, width, height, ...rest}) => (
  <PieGraph 
    dataset={dataset}
    radius={radius} 
    currentFilter={currentFilter}
    setCurrentFilter={onSetFilter}
    {...rest}
  />
)

let _Homepage = ({classes, dataset, filter, onSetFilter, currentFilter, ...rest}) => (
  <div className={classes.root} {...rest}>
    <MainChart 
      dataset={dataset} 
      onSetFilter={onSetFilter}
      currentFilter={currentFilter}
      radius={chartSizes.radius.large}
      className={classes.mainChart}
    />
    <Divider light/>
    <LegendChart className={classes.legend} dataset={dataset}/>
    <Divider light/>
    <TaskList
      tasks={tasksWithCostSelector(dataset)}
      fields={fields}
      default_order_by='total_cost'
      default_order='desc'
    />

  </div>
)

/*


*/

let styles = theme => ({
  root: theme.mixins.gutters({
    paddingTop: 0,
    paddingBottom: 16,
    overflowX: 'auto',
  }),
  mainChart: {
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit,
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  legend : {
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit,
    marginLeft: 'auto',
    marginRight: 'auto'
  }
})

export let Homepage = withStyles(styles)(_Homepage)


// let _TaskCosts = ({title, data, compact, onMore, onClose, classes, elevation, currentFilter, onSetFilter}) => (
//   <Paper className={classes.root} elevation={elevation}>
//     <ItemToolbar title={title} compact={compact} onMore={onMore} onClose={onClose} />
//     { ! hasCostDataSelector(data) &&
//       <Typography type="body1">No cost information</Typography>
//     }
//     { hasCostDataSelector(data) &&
//       <Grid container align="baseline" >
//         <Grid item sm={12} md={8}>
//           <PieGraph 
//             data={data}
//             radius={compact ? chartSizes.radius.small : chartSizes.radius.large} 
//             currentFilter={currentFilter}
//             setCurrentFilter={onSetFilter}
//           />
//         </Grid>
//         {compact &&
//         <Grid item sm={12}>
//           <Typography className={classes.tagline} type={appTheme.typography.tagline.type}>
//             {taglineSelector(data)}
//           </Typography>
//         </Grid>
//         }
//         {!compact &&
//         <Grid item  sm={12} md={4}>
//           <VictoryContainer width={200} height={200}>
//             <VictoryLegend 
//               colorScale={[chartColors.cost.fixed, chartColors.cost.resource, blueGrey[50]]}
//               data={legendDataSelector(data)}
//             />
//           </VictoryContainer>
//         </Grid>
//         }
//         {!compact && currentFilter == '' &&
//         <Grid item sm={12}>
//           <Divider light/>
//           <TaskList
//             tasks={mostExpensiveSelector(data)}
//             fields={fields}
//             default_order_by='total_cost'
//             default_order='desc'
//           />
//         </Grid>
//         }
//         {!compact && currentFilter != '' &&
//         <Grid item sm={12}>
//           <Divider light/>
//           <TaskList
//             tasks={filteredTasksSelector(data, currentFilter)}
//             fields={fields}
//           />
//         </Grid>
//         }
//       </Grid>
//     }
//   </Paper>
// )


// let _props = {
//   title: PropTypes.string,
//   data: PropTypes.shape({ 
//     cost: PropTypes.shape({
//       cost_type: PropTypes.shape({
//         resource_cost: PropTypes.number,
//         fixed_cost: PropTypes.number
//       })
//     })
//   }).isRequired,
//   compact: PropTypes.bool.isRequired,
//   onMore: PropTypes.func.isRequired,
//   onClose: PropTypes.func.isRequired,
// }

// _TaskCosts.propTypes = {
//   ..._props,
//   classes: PropTypes.object.isRequired,
//   ...taskFiltersProps(Filter)
// }

// let TaskCosts = withTaskFilters(Filter, Filter.none)(withStyles(styles)(_TaskCosts))

// export default TaskCosts

/*
"cost": {
  "total": 551157,
  "tasks": {
      "top": [
          {
              "name": "Window Systems(16)",
              "fixedCost": 0,
              "resourceCost": 18000,
              "cost": 18000
          },
          {
              "name": "Brick and vinyl siding(18)",
              "fixedCost": 0,
              "resourceCost": 8600,
              "cost": 8600
          },
          {
              "name": "Exterior trim(19)",
              "fixedCost": 0,
              "resourceCost": 2850,
              "cost": 2850
          },
          {
              "name": "Install heatpumps(22)",
              "fixedCost": 0,
              "resourceCost": 2450,
              "cost": 2450
          },
          {
              "name": "Erect walls, floors, an...(187)",
              "fixedCost": 0,
              "resourceCost": 18880,
              "cost": 18880
          }
      ]
  },
  "cost_type": {
      "fixed_cost": 188000,
      "resource_cost": 363157
  }
}

*/


// WEBPACK FOOTER //
// ./src/components/TaskCosts.js